import moment from "moment"
import { I18n } from "utils/i18n-shim"
import { array, bool, boolean, date, mixed, number, object, string } from "yup"
import { v4 as uuidv4 } from "uuid"
import min from "lodash/min"
import max from "lodash/max"
import kebabCase from "lodash/kebabCase"
import { flexPurchaseProduct } from "entities/purchase-product"
import numeral from "numeral"

export const getProductNameByLocale = (product, locale) => {
  return locale === "en" ? product.name_en || product.name : product.name
}

export const formatSalesProductShort = (salesProduct, locale = "de") => {
  const productName = getProductNameByLocale(salesProduct, locale)

  if (salesProduct?.type === "FlexProduct") {
    return `${productName} (${min(salesProduct.runtime_options)} - ${max(salesProduct.runtime_options)})`
  }
  return `${productName} (${salesProduct.runtime_in_days})`
}

export const formatSalesProduct = (salesProduct, locale = "de") => {
  const productName = getProductNameByLocale(salesProduct, locale)

  if (salesProduct.runtime_options?.length) {
    return `${salesProduct.supplier_name} | ${productName} (${min(salesProduct.runtime_options)} - ${max(salesProduct.runtime_options)})`
  }

  if (salesProduct.runtime_in_days) {
    return `${salesProduct.supplier_name} | ${productName} (${salesProduct.runtime_in_days})`
  }

  return `${salesProduct.supplier_name} | ${productName}`
}

export const getProductWithSupplier = (product, locale = "de") => {
  const productName = getProductNameByLocale(
    { name: product.product_name, name_en: product.product_name_en },
    locale
  )

  return `${product.supplier_name} - ${productName}`
}

export const runtimeTypes = [{ id: "fixed" }, { id: "automatic_renewal" }]

export const getRuntimeTypeLabel = (runtimeType) =>
  I18n.t(`product.runtime-type.${kebabCase(runtimeType.id)}`)

export const getProductTypes = (values) => {
  if (flexPurchaseProduct(values?.purchase_product_id?.purchase_type)) {
    return [{ id: "FlexProduct" }]
  }
  return [
    { id: "OnlineJobAd" },
    { id: "ApplicantDatabase" },
    { id: "AddOn" },
    { id: "CustomProduct" },
    { id: "FlexProduct" }
  ]
}

export const getProductTypeLabel = (productType) =>
  I18n.t(`product.product-type.${kebabCase(productType.id)}`)

export const createDefaultLocationCity = () => ({ id: null })

export const getLocationCities = () => {
  const locations = [createDefaultLocationCity()]

  for (let i = 1; i <= 10; i++) {
    locations.push({ id: i })
  }

  return locations
}

export const getLocationCityLabel = (locationCity) =>
  I18n.t("product.settings.locations.options.locations", {
    count: locationCity.id ?? 0
  })

export const findLocationCity = (locationCityId) => {
  return getLocationCities().find((city) => city.id === locationCityId)
}

export const createBlankProductPrice = () => ({
  virtualId: uuidv4(),
  id: null,
  from: "",
  to: "",
  market_price: "",
  price: "",
  lower_limit: ""
})

export const salesProductPriceSchema = object({
  rrp: string().price("price").required("required"),
  seller_lower_limit: string().price("price"),
  tl_price: string().price("price"),
  cross_selling_ids: mixed().transform((value) => value?.id),
  add_on_ids: array().transform((value) => value.map((addon) => addon.id)),
  online_job_ad_ids: array()
    .transform((value) => value.map((product) => product.id))
    .when("type", {
      is: (type) => type === "AddOn",
      then: (schema) => schema.min(1, "at-least-one-must-be-selected")
    }),
  min_price_in_package: string().price("price"),
  promotion_enabled: bool(),
  promotion_price: number().when("promotion_enabled", {
    is: Boolean,
    then: number().nullable().required("required"),
    otherwise: number().nullable()
  }),
  default_agency_price: string().when("default_agency_price_enabled", {
    is: Boolean,
    then: string().price("price").required("required"),
    otherwise: string().price("price")
  }),
  default_partner_price: string().when("default_partner_price_enabled", {
    is: Boolean,
    then: string().price("price").required("required"),
    otherwise: string().price("price")
  }),
  promotion_valid_until: date()
    .typeError("invalid-date")
    .nullable()
    .when("promotion_enabled", {
      is: Boolean,
      then: (schema) =>
        schema
          .required("required")
          .min(moment().startOf("day").add(1, "days").toDate(), "date-in-past")
    })
}).shape(
  {
    points: string().when("price_in_package", {
      is: (price_in_package) => !price_in_package,
      then: string().price("price").required("required"),
      otherwise: string().oneOf([""], "points-or-package-price")
    }),
    price_in_package: string().when("points", {
      is: (points) => !points,
      then: string().price("price").required("required"),
      otherwise: string().oneOf([""], "points-or-package-price")
    })
  },
  [["price_in_package", "points"]]
)

export const flexSalesProductPriceSchema = object({
  min_price: string().price("price").required("required"),
  max_price: string().price("price").required("required"),
  commission_percent: number().min(1).max(10).required("required"),
  promotion_enabled: bool(),
  promotion_price: number().when("promotion_enabled", {
    is: Boolean,
    then: number().nullable().required("required"),
    otherwise: number().nullable()
  }),
  promotion_valid_until: date()
    .typeError("invalid-date")
    .nullable()
    .when("promotion_enabled", {
      is: Boolean,
      then: (schema) =>
        schema
          .required("required")
          .min(moment().startOf("day").add(1, "days").toDate(), "date-in-past")
    })
})

const priceSchema = object({
  from: number().required("required").min(1).typeError("number"),
  to: number()
    .required("required")
    .typeError("number")
    .when("from", (from, schema) => {
      return from ? schema.min(from) : undefined
    }),
  market_price: string().price("price"),
  price: string().price("price").required("required"),
  rrp: string().price("price"),
  lower_limit: string().price("price"),
  _destroy: boolean()
})

export const gradedPricesSchema = object({
  product_prices_attributes: array()
    .of(priceSchema)
    .test("range", "ranges-must-not-overlap", (productPrices) => {
      let valid = true
      productPrices
        .filter((price) => !price._destroy)
        .forEach((productPrice, index) => {
          if (
            !productPrice.from ||
            !productPrice.to ||
            !productPrices[index - 1]?.to
          ) {
            return
          }
          if (
            (index === 0 &&
              productPrice.from === 1 &&
              productPrice.to >= productPrice.from) ||
            (productPrice.from > productPrices[index - 1].to &&
              productPrice.to >= productPrice.from)
          ) {
            return
          }
          valid = false
        })
      return valid
    })
    .min(1, "at-least-one-must-be-filled")
})

export const metadataSchema = object({
  product_setting_attributes: object({
    design_html: bool(),
    design_text: bool(),
    design_structured: bool(),
    tracking_views: bool(),
    tracking_clicks: bool(),
    cities: mixed()
      .transform((value) => value?.id)
      .nullable()
  })
})

export const salesProductSchema = object({
  id: number().nullable(),
  name: string().required("required"),
  name_en: string(),
  supplier_group_ids: mixed().transform((groups) =>
    groups.map((group) => group?.id)
  ),
  generic: bool().nullable(),
  type: mixed()
    .transform((value) => value?.id)
    .oneOf(getProductTypes().map((type) => type.id))
    .required("required"),
  supplier_id: mixed()
    .transform((value) => value?.id)
    .required("required"),
  ek_supplier_id: mixed()
    .transform((value) => value?.id)
    .required("required"),
  runtime_in_days: number().min(0).required("required"),
  runtime_type: mixed()
    .transform((value) => (value ? value.id : null))
    .oneOf(["fixed", "automatic_renewal", null])
    .nullable(),
  purchase_product_id: mixed()
    .transform((value) => value?.id)
    .required("required"),
  job_board: string().nullable(),
  country: mixed()
    .transform((values) =>
      values
        .map((value) => value?.id)
        .filter(Boolean)
        .join(",")
    )
    .required("required"),
  is_show: bool(),
  top: bool(),
  excluded_from_prediction: bool(),
  talent_bridge_application_provider_id: mixed()
    .transform((value) => value?.id)
    .nullable(),
  extending: bool(),
  expiring_in_days: number()
    .emptyToNull()
    .when("extending", {
      is: Boolean,
      then: (schema) => schema.required("required").min(1),
      otherwise: (schema) => schema.nullable()
    }),
  emails: mixed()
    .transform((value) => {
      return value.split(",").map((v) => v.trim())
    })
    .test("emailString", "should be email", (value) => {
      return array(string().email()).isValidSync(value)
    })
    .when("extending", {
      is: Boolean,
      then: array().required("required"),
      otherwise: array().nullable()
    })
})
export const flexSalesProductSchema = object({
  id: number().nullable(),
  name: string().required("required"),
  name_en: string(),
  supplier_group_ids: mixed().transform((groups) =>
    groups.map((group) => group?.id)
  ),
  generic: bool().nullable(),
  type: mixed()
    .transform((value) => value?.id)
    .oneOf(getProductTypes().map((type) => type.id))
    .required("required"),
  supplier_id: mixed()
    .transform((value) => value?.id)
    .required("required"),
  ek_supplier_id: mixed()
    .transform((value) => value?.id)
    .required("required"),
  runtime_options: array()
    .required("required")
    .of(number().typeError("Must be a number"))
    .min(1, "At least one number is required")
    .required("required"),
  runtime_type: mixed()
    .transform((value) => (value ? value.id : null))
    .oneOf(["fixed", "automatic_renewal", null])
    .nullable(),
  purchase_product_id: mixed()
    .transform((value) => value?.id)
    .required("required"),
  job_board: string().nullable(),
  country: mixed()
    .transform((values) =>
      values
        .map((value) => value?.id)
        .filter(Boolean)
        .join(",")
    )
    .required("required"),
  is_show: bool(),
  top: bool(),
  excluded_from_prediction: bool(),
  talent_bridge_application_provider_id: mixed()
    .transform((value) => value?.id)
    .nullable(),
  extending: bool(),
  expiring_in_days: number()
    .emptyToNull()
    .when("extending", {
      is: Boolean,
      then: (schema) => schema.required("required").min(1),
      otherwise: (schema) => schema.nullable()
    }),
  emails: mixed()
    .transform((value) => {
      return value.split(",").map((v) => v.trim())
    })
    .test("emailString", "should be email", (value) => {
      return array(string().email()).isValidSync(value)
    })
    .when("extending", {
      is: Boolean,
      then: array().required("required"),
      otherwise: array().nullable()
    })
})

export const getShowPackagePrice = ({ formik, products }) => {
  const selectedIds = formik.values.selected_products

  if (!products) {
    return selectedIds.length > 1
  }

  const affiliateProductIds = products
    .filter((product) => product.type === "AffiliateProduct")
    .map((product) => product.id)

  return (
    selectedIds.filter((id) => !affiliateProductIds.includes(id)).length > 1
  )
}

export const getSelectedAddOns = ({ product, formik }) => {
  return product.add_ons.filter((addOn) => {
    const selectedProductAddOns = formik.values.addOns[product.id]
    if (!selectedProductAddOns) {
      return false
    }
    return selectedProductAddOns.includes(addOn.id)
  })
}

const dynamicFieldSchema = object({
  name: string().required("required"),
  technical_name: string().required("required"),
  is_mandatory: boolean(),
  count: mixed().when("enabled", {
    is: true,
    then: number().typeError("number").required("required")
  }),
  length: mixed().when("enabled", {
    is: true,
    then: number().typeError("number").required("required")
  }),
  not_removable: boolean(),
  enabled: boolean().required("required")
})

export const dynamicFieldsSchema = object({
  dynamic_fields: array().of(dynamicFieldSchema)
})

export const getDiscountPrice = ({
  product,
  showPackagePrice,
  flexProductDatum = null
}) => {
  if (flexProductDatum) {
    let price = flexProductDatum.price

    if (product.affiliate_price_discount_percent) {
      price = (1 - product.affiliate_price_discount_percent / 100.0) * price
    }
    return numeral(price).format("0,0.00")
  }

  return showPackagePrice
    ? product.calculated_single_package_price
    : product.customer_price ?? product.market_price
}
